/**
 * Who may say what other people can reach.
 *
 * An application answers this on its own payload, in `user_can_configure_access`
 * -- true for its creator and for the workspace admins. The same answer governs
 * everything finer inside it: restricting one of its tables, and redacting one
 * of its columns. So a control for either belongs to whoever may configure the
 * database that holds it.
 *
 * Pure, apart from the hook at the bottom that reads the cache for it.
 */
import { useQuery } from '@tanstack/react-query'

import type { Application, DatabaseApplication } from '@/api/types'
import { applicationsQuery } from './queries'

/**
 * Whether this reader may govern who reaches a table, and the columns in it.
 *
 * The database is found by looking for the one that has the table rather than
 * being passed in, the way `tablesBeside` finds it: the applications cache
 * already holds every table of every database in the workspace, and a second
 * source for it would be a second thing to keep current.
 *
 * `false` while the cache is still filling, which is the safe direction -- a
 * control that appears once the answer arrives is better than one that is
 * offered and then taken away.
 */
export function mayGovernTable(
  applications: readonly Application[] | undefined,
  tableId: number,
): boolean {
  const database = (applications ?? [])
    .filter(
      (application): application is DatabaseApplication =>
        application.type === 'database',
    )
    .find((application) => application.tables.some((table) => table.id === tableId))

  return database?.user_can_configure_access ?? false
}

/**
 * `mayGovernTable`, against the workspace's applications as they are cached.
 *
 * The workspace is read off a field by the callers, so it is missing for as
 * long as the fields are -- and there is no workspace-less list to ask for.
 */
export function useMayGovernTable(
  workspaceId: number | undefined,
  tableId: number,
): boolean {
  const { data } = useQuery({
    ...applicationsQuery(workspaceId ?? 0),
    enabled: workspaceId !== undefined,
  })
  return mayGovernTable(data, tableId)
}
